import { useEffect, useState } from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import { ArbiterShell } from "@/components/ArbiterShell";
import { getApiErrorDetail } from "@/lib/api";
import { getRiskRegister, type RiskRegisterEntry } from "@/lib/dpoApi";

const GOLD = "oklch(0.695 0.105 68)";
const BORDER = "color-mix(in oklch, oklch(0.695 0.105 68) 18%, transparent)";

// Couleur du badge de niveau de risque.
function niveauColor(niveau: string): string {
  switch (niveau) {
    case "CRITIQUE":
      return "oklch(0.55 0.22 25)"; // rouge
    case "ELEVE":
      return "oklch(0.70 0.17 50)"; // orange
    case "MODERE":
      return GOLD;
    default:
      return "oklch(0.6 0.02 243)"; // FAIBLE / inconnu
  }
}

export default function DpoConformite() {
  const [rows, setRows] = useState<RiskRegisterEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getRiskRegister()
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          getApiErrorDetail(err) ??
            "Registre indisponible (accès réservé au DPO).",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ArbiterShell title="Conformité — Registre des risques IA">
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm" style={{ color: GOLD }}>
          <ShieldCheck size={16} />
          Registre des risques (AI Act Art. 9) — supervision DPO
        </div>

        {error && (
          <div
            className="rounded-md border p-4 text-sm"
            style={{ borderColor: BORDER, opacity: 0.85 }}
          >
            {error}
          </div>
        )}

        {!error && rows === null && (
          <div className="flex items-center gap-2 text-sm" style={{ opacity: 0.7 }}>
            <Loader2 size={14} className="animate-spin" /> Chargement…
          </div>
        )}

        {!error && rows && rows.length === 0 && (
          <p className="text-sm" style={{ opacity: 0.7 }}>
            Registre vide.
          </p>
        )}

        {!error && rows && rows.length > 0 && (
          <ul className="space-y-2">
            {rows.map((r) => (
              <li
                key={r.id}
                className="rounded-md border p-3 space-y-1.5"
                style={{ borderColor: BORDER }}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[11px]" style={{ opacity: 0.6 }}>
                    {r.code}
                  </span>
                  <span className="text-sm font-medium">{r.libelle}</span>
                  <span
                    className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold"
                    style={{ background: niveauColor(r.niveau), color: "oklch(0.15 0 0)" }}
                  >
                    {r.niveau}
                  </span>
                </div>
                <div className="text-[11px]" style={{ opacity: 0.7 }}>
                  Probabilité : {r.probabilite} · Impact : {r.impact} · Occurrences :{" "}
                  {r.occurrences}
                </div>
                {r.mesures_existantes.length > 0 && (
                  <div className="text-[11px]">
                    <span style={{ color: GOLD }}>Mesures existantes : </span>
                    {r.mesures_existantes.join(", ")}
                  </div>
                )}
                {r.mesures_planifiees.length > 0 && (
                  <div className="text-[11px]" style={{ opacity: 0.8 }}>
                    <span style={{ color: GOLD }}>Mesures planifiées : </span>
                    {r.mesures_planifiees.join(", ")}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </ArbiterShell>
  );
}
